// src/context/AuthContext.js
import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import { getCurrentUser, loginUser, registerUser, registerSocialUser, logoutUser } from "../Utils/authService";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Auth state is derived from the server — no localStorage for tokens
  const [userRole, setUserRole] = useState(
    localStorage.getItem("userRole") || null
  );
  const [userEmail, setUserEmail] = useState(
    localStorage.getItem("userEmail") || null
  );
  const [displayName, setDisplayName] = useState(
    localStorage.getItem("displayName") || null
  );
  // isAuthenticated replaces token — true when /api/Users/me succeeds
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // On mount, check if the browser already has a valid auth cookie
  useEffect(() => {
    const checkSession = async () => {
      try {
        const profile = await getCurrentUser();
        const name = [profile?.firstName, profile?.lastName]
          .filter(Boolean)
          .join(" ")
          .trim();
        const role = profile?.roles?.[0] || localStorage.getItem("userRole") || null;
        const email = profile?.email || localStorage.getItem("userEmail") || null;
        setIsAuthenticated(true);
        setUserRole(role);
        setUserEmail(email);
        if (name) {
          setDisplayName(name);
          localStorage.setItem("displayName", name);
        }
        if (role) localStorage.setItem("userRole", role);
        if (email) localStorage.setItem("userEmail", email);
      } catch {
        // No valid session — user is not logged in
        setIsAuthenticated(false);
      } finally {
        setAuthChecked(true);
      }
    };
    checkSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (credentials) => {
    const response = await loginUser(credentials);
    // Token is now in HTTP-only cookie — response.token is null
    // Re-fetch profile to populate auth state
    try {
      const profile = await getCurrentUser();
      const name = [profile?.firstName, profile?.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      const role = profile?.roles?.[0] || "Client";
      const email = profile?.email || response?.email || null;
      setIsAuthenticated(true);
      setUserRole(role);
      setUserEmail(email);
      if (name) {
        setDisplayName(name);
        localStorage.setItem("displayName", name);
      }
      localStorage.setItem("userRole", role);
      if (email) localStorage.setItem("userEmail", email);
      return { role, email };
    } catch {
      throw new Error("Login succeeded but profile fetch failed.");
    }
  };

  const register = async (formData, socialKey = null) => {
    const response = socialKey ? await registerSocialUser(formData, socialKey) : await registerUser(formData);

    // A farmer awaiting approval gets no session. Previously this fell through to
    // the "registration returned a session" branch below, where getCurrentUser()
    // failed and the catch discarded the reason — so the farmer was redirected to
    // /login with nothing explaining why. Report it explicitly instead.
    if (response?.requiresApproval) {
      return {
        role: formData.userRole === "Villager" ? "Villager" : "Client",
        email: response?.email || formData.email,
        requiresApproval: true,
        requiresEmailConfirmation: response?.requiresEmailConfirmation === true,
        message: response?.message || null,
      };
    }

    if (response?.requiresEmailConfirmation !== false) {
      return {
        role: formData.userRole === "Villager" ? "Villager" : "Client",
        email: response?.email || formData.email,
        requiresEmailConfirmation: true,
        message: response?.message || null,
      };
    }
    // Registration returned a session — fetch profile
    try {
      const profile = await getCurrentUser();
      const role = profile?.roles?.[0] || (formData.userRole === "Villager" ? "Villager" : "Client");
      const email = profile?.email || response?.email || formData.email;
      setIsAuthenticated(true);
      setUserRole(role);
      setUserEmail(email);
      localStorage.setItem("userRole", role);
      if (email) localStorage.setItem("userEmail", email);
      return { role, email, requiresEmailConfirmation: false };
    } catch {
      return {
        role: formData.userRole === "Villager" ? "Villager" : "Client",
        email: response?.email || formData.email,
        requiresEmailConfirmation: false,
      };
    }
  };

  const logout = useCallback(async () => {
    await logoutUser(); // Clears HTTP-only cookie on backend
    localStorage.removeItem("userRole");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("displayName");
    setIsAuthenticated(false);
    setUserRole(null);
    setUserEmail(null);
    setDisplayName(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        authChecked,
        userRole,
        userEmail,
        displayName,
        login,
        register,
        logout,
        // token is intentionally omitted — use isAuthenticated instead
        // kept as null for backward compatibility with any code reading token
        token: isAuthenticated ? true : null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
